import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["street", "suggestions", "city", "state", "zip"]

  connect() {
    this._debounceTimer = null
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
  }

  onInput() {
    const query = this.streetTarget.value.trim()
    clearTimeout(this._debounceTimer)

    if (query.length < 4) {
      this.hideSuggestions()
      return
    }

    this._debounceTimer = setTimeout(() => this.fetchSuggestions(query), 300)
  }

  async fetchSuggestions(query) {
    const params = new URLSearchParams({
      q: query,
      countrycodes: "us",
      format: "json",
      addressdetails: "1",
      limit: "6"
    })

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        { headers: { "Accept-Language": "en" } }
      )
      const results = await res.json()
      this.renderSuggestions(results)
    } catch {
      this.hideSuggestions()
    }
  }

  renderSuggestions(results) {
    const addressResults = results.filter(r => r.address?.road)

    if (addressResults.length === 0) {
      this.hideSuggestions()
      return
    }

    this.suggestionsTarget.innerHTML = addressResults.map((r, i) =>
      `<div class="suggestion-item px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
            data-index="${i}"
            data-display="${this.displayLine(r.address)}"
            data-street="${this.streetLine(r.address)}"
            data-city="${this.cityName(r.address)}"
            data-state="${this.stateCode(r.address)}"
            data-zip="${r.address.postcode || ""}">
         <span class="font-medium">${this.streetLine(r.address)}</span>
         <span class="text-gray-400 ml-1">${this.cityName(r.address)}, ${this.stateCode(r.address)} ${r.address.postcode || ""}</span>
       </div>`
    ).join("")

    this.suggestionsTarget.classList.remove("hidden")
  }

  select(event) {
    const item = event.target.closest(".suggestion-item")
    if (!item) return

    this.streetTarget.value  = item.dataset.street
    this.cityTarget.value    = item.dataset.city
    this.stateTarget.value   = item.dataset.state.toUpperCase()
    this.zipTarget.value     = item.dataset.zip

    this.hideSuggestions()
    this.zipTarget.focus()
  }

  hideSuggestions() {
    this.suggestionsTarget.innerHTML = ""
    this.suggestionsTarget.classList.add("hidden")
  }

  onKeydown(event) {
    if (event.key === "Escape") this.hideSuggestions()
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.hideSuggestions()
  }

  // ── helpers ──

  streetLine(a) {
    return [a.house_number, a.road].filter(Boolean).join(" ")
  }

  cityName(a) {
    return a.city || a.town || a.village || a.hamlet || a.county || ""
  }

  stateCode(a) {
    return a["ISO3166-2-lvl4"]?.replace("US-", "") || a.state_code || a.state || ""
  }

  displayLine(a) {
    return [this.streetLine(a), this.cityName(a), this.stateCode(a), a.postcode].filter(Boolean).join(", ")
  }
}
